import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { BaseButton, CloseButton, formatDateToShortDotV2 } from '@repo/common';
import Resume from '../../../JobBoard/Apply/Resume';
import { getEmployerApplicationDetail } from '../../../../api/jobboard/apply.api';

export const safeDocumentUrl = (value) => {
  try {
    const url = new URL(value);
    return ['https:', 'http:'].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
};

const STATUS_LABELS = { SUBMITTED: '확인필요', ACCEPTED: '수락', NEEDPREP: '탈락' };

const EmployerApplicationModal = ({ applicationId, onClose, isMobile }) => {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const dialogRef = useRef(null);

  useEffect(() => {
    let active = true;
    setDetail(null);
    setError('');
    getEmployerApplicationDetail(applicationId).then((data) => {
      if (active) setDetail(data);
    }).catch((failure) => {
      if (!active) return;
      setError([403, 404].includes(failure.response?.status)
        ? '열람 권한이 없거나 지원이 취소되어 더 이상 확인할 수 없는 지원서입니다. 보관 기한이 만료된 경우에도 열람할 수 없습니다.'
        : '지원서를 불러오지 못했습니다. 다시 시도해 주세요.');
    });
    return () => { active = false; };
  }, [applicationId, attempt]);

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus?.();
    };
  }, []);

  const onKeyDown = (event) => {
    // Nested resume previews are rendered in their own portal.
    if (!dialogRef.current?.contains(event.target)) return;
    if (event.key === 'Escape') onClose();
    if (event.key !== 'Tab') return;
    const focusable = [...dialogRef.current.querySelectorAll('button:not(:disabled), a[href], [tabindex="0"]')];
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
      event.preventDefault(); last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault(); first?.focus();
    }
  };

  return ReactDOM.createPortal(
    <div className='fixed inset-0 z-[1000000] flex items-stretch justify-center bg-surface-black60 tabletH:items-center tabletH:p-8' onClick={onClose}>
      <section ref={dialogRef} tabIndex={-1} role='dialog' aria-modal='true' aria-labelledby='employer-application-title' onKeyDown={onKeyDown} onClick={(event) => event.stopPropagation()} className='flex w-full max-w-[800px] max-h-full flex-col bg-grayscale-gray01 tabletH:rounded-[24px] outline-none'>
        <header className='flex shrink-0 items-center justify-between border-b border-grayscale-gray02 px-6 py-5'>
          <h2 id='employer-application-title' className='typo-b18Strong text-text-primary'>지원서 상세</h2>
          <CloseButton type='default' onClick={onClose} />
        </header>
        <div className='min-h-0 overflow-y-auto p-6'>
          {error ? (
            <div className='flex flex-col items-start gap-4'>
              <p role='alert' className='typo-b14 text-text-secondary'>{error}</p>
              <BaseButton size='s' hideIcon onClick={() => setAttempt((value) => value + 1)}>다시 시도</BaseButton>
            </div>
          ) : !detail ? <p role='status'>지원서를 불러오는 중…</p> : (
            <div className='flex flex-col gap-6'>
              <div className='rounded-xl bg-grayscale-gray00 p-5'>
                <p className='typo-b14Strong text-text-secondary'>{detail.companyName}</p>
                <h3 className='mt-1 typo-b18Strong text-text-primary'>{detail.jobTitle}</h3>
                <p className='mt-2 typo-b13 text-text-secondary'>지원일 {formatDateToShortDotV2(detail.createdAt)} · {STATUS_LABELS[detail.status] || detail.status}</p>
              </div>
              <section>
                <h3 className='mb-3 typo-b16Strong'>지원자 정보</h3>
                <dl className='grid grid-cols-[90px_minmax(0,1fr)] gap-x-4 gap-y-3 typo-b14 break-words'>
                  <dt>이름</dt><dd>{[detail.firstName, detail.lastName].filter(Boolean).join(' ') || '—'}</dd>
                  <dt>이메일</dt><dd>{detail.email || '—'}</dd>
                  <dt>연락처</dt><dd>{[detail.phoneCountry, detail.phone].filter(Boolean).join(' ') || '—'}</dd>
                  <dt>비자</dt><dd>{detail.visaType || '—'}</dd>
                </dl>
              </section>
              <section>
                <p className='typo-b13 text-text-secondary'>해당 지원 건에 보관된 제출 이력서입니다. 이후 원본을 수정해도 제출본은 바뀌지 않습니다.</p>
                <Resume status='view' selectedResume={detail.resume} $isMobile={isMobile} />
              </section>
              <section>
                <h3 className='mb-3 typo-b16Strong'>첨부 자료</h3>
                {!detail.files?.length && <p className='typo-b14 text-text-secondary'>첨부 파일이 없습니다.</p>}
                <ul className='flex flex-col gap-3'>
                  {(detail.files || []).map((file) => (
                    <li key={file.id} className='break-all typo-b14'>
                      {safeDocumentUrl(file.fileUrl)
                        ? <a className='text-text-primary underline underline-offset-4' href={safeDocumentUrl(file.fileUrl)} target='_blank' rel='noopener noreferrer'>{file.fileName} · 다운로드</a>
                        : <span>{file.fileName} · 파일을 열 수 없습니다.</span>}
                    </li>
                  ))}
                </ul>
              </section>
              <section>
                <h3 className='mb-3 typo-b16Strong'>제출 링크</h3>
                {!detail.links?.length && <p className='typo-b14 text-text-secondary'>제출한 링크가 없습니다.</p>}
                <ul className='flex flex-col gap-3'>
                  {(detail.links || []).map((link) => (
                    <li key={link.id} className='break-all typo-b14'>
                      <span>{link.linkType || '링크'}: </span>
                      {safeDocumentUrl(link.linkUrl)
                        ? <a className='underline underline-offset-4' href={safeDocumentUrl(link.linkUrl)} target='_blank' rel='noopener noreferrer'>{link.linkUrl}</a>
                        : <span>{link.linkUrl || '—'}</span>}
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          )}
        </div>
        <footer className='flex shrink-0 justify-end border-t border-grayscale-gray02 p-4'>
          <BaseButton size='m' hideIcon onClick={onClose}>닫기</BaseButton>
        </footer>
      </section>
    </div>, document.body,
  );
};

export default EmployerApplicationModal;
